import React, { useState } from 'react';
import { useData } from '../../contexts/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit3, FiTrash2, FiTrendingUp, FiHome, FiBookOpen, FiSave } = FiIcons;

const ScenarioManager = () => {
  const { scenarios, saveScenario, deleteScenario } = useData();
  const [showForm, setShowForm] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'retirement',
    parameters: {}
  });

  const scenarioTypes = [
    { value: 'retirement', label: 'Retirement Planning', icon: FiTrendingUp },
    { value: 'home', label: 'Home Purchase', icon: FiHome },
    { value: 'education', label: 'Education Planning', icon: FiBookOpen }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingScenario) {
      // Update existing scenario logic would go here
      setEditingScenario(null);
    } else {
      saveScenario(formData);
    }
    setFormData({ name: '', description: '', type: 'retirement', parameters: {} });
    setShowForm(false);
  };

  const handleEdit = (scenario) => {
    setEditingScenario(scenario);
    setFormData(scenario);
    setShowForm(true);
  };

  const handleDelete = (scenarioId) => {
    if (window.confirm('Are you sure you want to delete this scenario?')) {
      deleteScenario(scenarioId);
    }
  };

  const getTypeIcon = (type) => {
    const typeConfig = scenarioTypes.find(t => t.value === type);
    return typeConfig ? typeConfig.icon : FiTrendingUp;
  };

  const getTypeLabel = (type) => {
    const typeConfig = scenarioTypes.find(t => t.value === type);
    return typeConfig ? typeConfig.label : 'Unknown';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Portfolio Scenarios</h2>
          <p className="text-gray-600">Create and manage different financial planning scenarios</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          <span>New Scenario</span>
        </button>
      </div>

      {/* Scenario Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            {editingScenario ? 'Edit Scenario' : 'Create New Scenario'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scenario Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Conservative Retirement Plan"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows="3"
                placeholder="Describe this scenario..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scenario Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {scenarioTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
              >
                <SafeIcon icon={FiSave} className="w-4 h-4" />
                <span>{editingScenario ? 'Update' : 'Save'} Scenario</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingScenario(null);
                  setFormData({ name: '', description: '', type: 'retirement', parameters: {} });
                }}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Scenarios List */}
      {scenarios.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {scenarios.map((scenario) => (
            <div key={scenario.id} className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={getTypeIcon(scenario.type)} className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{scenario.name}</h3>
                    <p className="text-sm text-gray-500">{getTypeLabel(scenario.type)}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(scenario)}
                    className="text-gray-400 hover:text-primary-600 transition-colors"
                  >
                    <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(scenario.id)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {scenario.description && (
                <p className="text-gray-600 text-sm mb-4">{scenario.description}</p>
              )}
              
              <div className="text-xs text-gray-500">
                Created: {new Date(scenario.createdAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <SafeIcon icon={FiPlus} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No Scenarios Yet
          </h3>
          <p className="text-gray-600 mb-6">
            Create your first scenario to start organizing your financial plans
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center space-x-2 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            <span>Create First Scenario</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ScenarioManager;